import { getBuildingMembersReport } from "../../../Redux/Reports/ReportsActions";
import { getAllBuildings } from "../../../Redux/Buildings/BuildingActions";
import Loading from "../../../Components/Common/Loading/Loading";
import { AppDispatch, RootState } from "../../../Redux/store";
import { ColDef, GridReadyEvent } from "ag-grid-community";
import exportToExcel from "../../../Util/exportToExcel";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import styles from "./MemberReport.module.scss";
import { IoCloseSharp } from "react-icons/io5";
import { AgGridReact } from "ag-grid-react";
import moment from "moment";

interface MemberReportType {
  buildingName: string;
  memberFullName: string;
  leaseUnit: string;
  dateJoined: string;
  packageName: string;
  packagePrice: number;
  memberId: number;
  buildingId: number;
  startDate: string;
  totalBilled: number;
  lastLoginTime: string;
}

const defaultColDef: ColDef = {
  resizable: true,
  sortable: true,
  filter: false,
  wrapText: true,
  autoHeight: true,
  unSortIcon: true,
};

const columnDefs: ColDef<MemberReportType>[] = [
  {
    headerName: "Building",
    field: "buildingName",
    minWidth: 150,
    flex: 1,
  },

  {
    headerName: "Unit",
    field: "leaseUnit",
    minWidth: 150,
    flex: 1,
  },

  {
    headerName: "Member",
    field: "memberFullName",
    minWidth: 150,
    flex: 1,
  },
  {
    headerName: "Last Login",
    field: "lastLoginTime",
    minWidth: 150,
    flex: 1,
    valueGetter: (params) => {
      return params?.data?.lastLoginTime
        ? moment(params?.data?.lastLoginTime).format("MM/DD/YYYY HH:mm:ss")
        : "N/A";
    },
  },

  {
    headerName: "Date Joined",
    field: "dateJoined",
    minWidth: 150,
    valueGetter: (params) => {
      return moment(params?.data?.dateJoined).format("MM/DD/YYYY");
    },
    flex: 1,
  },

  {
    headerName: "Package",
    field: "packageName",
    minWidth: 150,
    flex: 1,
  },

  {
    headerName: "Total Billed",
    field: "totalBilled",
    minWidth: 150,
    flex: 1,
    valueFormatter: (params) => (params?.value ? `$ ${params.value}` : ""),
  },
];

function MemberReport() {
  const [rowData, setRowData] = useState<MemberReportType[]>([]);
  const [gridApi, setGridApi] = useState<any>(null);
  const [search, setSearch] = useState<string>("");
  const [buildingFilter, setBuildingFilter] = useState<string>("");
  const dispatch = useDispatch<AppDispatch>();
  const { buildingMembersReport, buildingMembersReportLoading } = useSelector(
    (state: RootState) => state.reports,
  );
  const { buildings } = useSelector((state: RootState) => state.buildings);
  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params?.api);
    params?.api?.sizeColumnsToFit();
  };

  const applyFilters = (rowData: MemberReportType[]) => {
    let array = [...rowData];

    if (search?.length > 3) {
      array = array.filter(
        (member: MemberReportType) =>
          member?.buildingName
            ?.toLowerCase()
            ?.includes(search?.toLowerCase()) ||
          member?.packageName?.toLowerCase()?.includes(search?.toLowerCase()) ||
          member?.memberFullName
            ?.toLowerCase()
            ?.includes(search?.toLowerCase()) ||
          member?.leaseUnit?.toLowerCase()?.includes(search?.toLowerCase()),
      );
    }

    if (buildingFilter) {
      array = array.filter(
        (member: MemberReportType) => member?.buildingName === buildingFilter,
      );
    }

    return array;
  };

  const filteredRowData = applyFilters(rowData);

  useEffect(() => {
    if (!gridApi) return;

    const handleResize = () => {
      gridApi?.sizeColumnsToFit();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [gridApi]);

  useEffect(() => {
    dispatch(getBuildingMembersReport());
    dispatch(getAllBuildings());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (buildingMembersReport) {
      setRowData(buildingMembersReport);
    }
  }, [buildingMembersReport]);

  if (buildingMembersReportLoading) {
    return <Loading />;
  }

  return (
    <div className={styles.mainContainer}>
      <div className={styles.topContainer}>
        <div className={styles.headingContainer}>
          <h1 className={styles.heading}>Member Report</h1>
        </div>
        <div className={styles.buttonContainer}>
          {filteredRowData?.length > 0 && (
            <button
              className={styles.button}
              onClick={() =>
                exportToExcel({
                  data: filteredRowData || [],
                  fileName: "Member Report",
                })
              }
            >
              Export
            </button>
          )}
        </div>
      </div>
      <div className={styles.bodyContainer}>
        <div className={styles.doubleInputContainer}>
          <div className={styles.searchContainer}>
            <div className={styles.searchContainerInner}>
              <input
                type="text"
                className={styles.input}
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e?.target?.value || "")}
              />
              <div
                className={styles.iconContainer}
                onClick={() => setSearch("")}
              >
                <IoCloseSharp className={styles.icon} />
              </div>
            </div>
          </div>
          <div className={styles.filtersContainer}>
            <div className={styles.filterItem}>
              <p className={styles.label}>Building</p>
              <select
                className={styles.input}
                value={buildingFilter}
                onChange={(e) => setBuildingFilter(e?.target?.value || "")}
              >
                <option value="">All</option>
                {buildings?.map((building: any, i: number) => {
                  return (
                    <option key={i} value={building?.buildingName}>
                      {building?.buildingName}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        </div>
        <div className={styles.gridContainer}>
          <AgGridReact
            className={styles.grid}
            rowData={filteredRowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            pagination={true}
            paginationPageSize={10}
            paginationPageSizeSelector={[5, 10, 20, 50, 100]}
            animateRows={true}
            domLayout="autoHeight"
          />
        </div>
      </div>
    </div>
  );
}

export default MemberReport;
